import {
	formatCompactCurrency,
	formatPrice,
	showSliderMode,
	showOptionsMode,
	updateCalculatorUi,
	debounce,
	syncStableCardHeight,
  } from "./your-investment-ui";
  
  const INVESTMENT_CONFIG = {
	organisations: {
	  mode: "bands",
	  defaultIndex: 0,
	  primaryLabel: "Estimated annual revenue:",
	  qualifier: "Up to",
	  bands: [
		{
		  displayLabel: "Free Membership",
		  membershipLabel: "Free Membership",
		  endValue: 25000,
		  price: 0,
		},
		{
		  displayLabel: "Small Organisations",
		  membershipLabel: "€25,001 to €75,000 (€75)",
		  endValue: 75000,
		  price: 75,
		},
		{
		  displayLabel: "Medium Organisations",
		  membershipLabel: "€75,001 to €250,000 (€150)",
		  endValue: 250000,
		  price: 150,
		},
		{
		  displayLabel: "Medium Organisations",
		  membershipLabel: "€250,001 to €500,000 (€330)",
		  endValue: 500000,
		  price: 330,
		},
		{
		  displayLabel: "Medium Organisations",
		  membershipLabel: "€500,001 to €1,000,000 (€450)",
		  endValue: 1000000,
		  price: 450,
		},
		{
		  displayLabel: "Large Organisations",
		  membershipLabel: "€1,000,001 to €5,000,000 (€600)",
		  endValue: 5000000,
		  price: 600,
		},
		{
		  displayLabel: "Large Organisations",
		  membershipLabel: "€5,000,001 upwards (€750)",
		  endValue: 999999999,
		  price: 750,
		},
	  ],
	},
  
	individuals: {
	  mode: "options",
	  defaultIndex: 0,
	  primaryLabel: "Membership type:",
	  qualifier: "",
	  options: [
		{
		  displayLabel: "Unwaged",
		  membershipLabel: "Individual Unwaged €25",
		  price: 25,
		},
		{
		  displayLabel: "Waged",
		  membershipLabel: "Individual Waged €75",
		  price: 75,
		},
	  ],
	},
  
	associate: {
	  mode: "bands",
	  defaultIndex: 0,
	  primaryLabel: "Estimated annual revenue:",
	  qualifier: "Up to",
	  bands: [
		{
		  displayLabel: "Associate Membership",
		  membershipLabel: "Up to €25,000 (€25)",
		  endValue: 25000,
		  price: 25,
		},
		{
		  displayLabel: "Associate Membership",
		  membershipLabel: "€25,001 to €75,000 (€75)",
		  endValue: 75000,
		  price: 75,
		},
		{
		  displayLabel: "Associate Membership",
		  membershipLabel: "€75,001 to €250,000 (€150)",
		  endValue: 250000,
		  price: 150,
		},
		{
		  displayLabel: "Associate Membership",
		  membershipLabel: "€250,001 to €500,000 (€330)",
		  endValue: 500000,
		  price: 330,
		},
		{
		  displayLabel: "Associate Membership",
		  membershipLabel: "€500,001 to €1,000,000 (€450)",
		  endValue: 1000000,
		  price: 450,
		},
		{
		  displayLabel: "Associate Membership",
		  membershipLabel: "€1,000,001 to €5,000,000 (€600)",
		  endValue: 5000000,
		  price: 600,
		},
		{
		  displayLabel: "Associate Membership",
		  membershipLabel: "€5,000,001 upwards (€750)",
		  endValue: 999999999,
		  price: 750,
		},
	  ],
	},
  };
  
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  
  const setQueryParam = (params, key, value) => {
	if (value === null || value === undefined || value === "") {
	  params.delete(key);
	  return;
	}
  
	params.set(key, String(value));
  };
  
  const getOrganisationTypeParam = (typeKey) => {
	if (typeKey === "individuals") {
	  return "Individual";
	}
  
	return "";
  };
  
  const buildCtaUrl = (baseUrl, data) => {
	if (!baseUrl) {
	  return "";
	}
  
	const url = new URL(baseUrl, window.location.origin);
  
	setQueryParam(url.searchParams, "membership_type", data.typeKey);
	setQueryParam(url.searchParams, "membership_label", data.membershipLabel);
	setQueryParam(
	  url.searchParams,
	  "organisation_type",
	  getOrganisationTypeParam(data.typeKey)
	);
  
	return url.toString();
  };
  
  const getSelection = (typeConfig, nextIndex) => {
	if (typeConfig.mode === "options") {
	  const options = typeConfig.options || [];
	  const safeIndex = clamp(
		Number(nextIndex ?? typeConfig.defaultIndex ?? 0),
		0,
		options.length - 1
	  );
	  const option = options[safeIndex];
  
	  return {
		index: safeIndex,
		membershipLabel: option.membershipLabel,
		displayValue: option.displayLabel,
		price: option.price,
		revenueUpTo: null,
		primaryLabel: typeConfig.primaryLabel,
		qualifier: typeConfig.qualifier,
		showSlider: false,
		options,
	  };
	}
  
	const bands = typeConfig.bands || [];
	const safeIndex = clamp(
	  Number(nextIndex ?? typeConfig.defaultIndex ?? 0),
	  0,
	  bands.length - 1
	);
	const band = bands[safeIndex];
  
	return {
	  index: safeIndex,
	  membershipLabel: band.membershipLabel,
	  displayValue: formatCompactCurrency(band.endValue),
	  price: band.price,
	  revenueUpTo: band.endValue,
	  primaryLabel: typeConfig.primaryLabel,
	  qualifier: typeConfig.qualifier,
	  showSlider: true,
	  sliderMax: Math.max(bands.length - 1, 0),
	};
  };
  
  const createSelectedIndexes = () => {
	return {
	  organisations: INVESTMENT_CONFIG.organisations.defaultIndex,
	  individuals: INVESTMENT_CONFIG.individuals.defaultIndex,
	  associate: INVESTMENT_CONFIG.associate.defaultIndex,
	};
  };
  
  const getDomElements = (block) => {
	return {
	  calculator: block.querySelector('[data-js="investment-calculator"]'),
	  slider: block.querySelector('[data-js="investment-slider"]'),
	  sliderWrap: block.querySelector('[data-js="investment-slider-wrap"]'),
	  optionsWrap: block.querySelector('[data-js="investment-options"]'),
	  primaryLabel: block.querySelector('[data-js="investment-primary-label"]'),
	  qualifier: block.querySelector('[data-js="investment-qualifier"]'),
	  revenueValue: block.querySelector('[data-js="investment-revenue"]'),
	  priceValue: block.querySelector('[data-js="investment-price"]'),
	  tabs: block.querySelectorAll('[data-js="investment-tab"]'),
	  cta: block.querySelector('[data-js="investment-cta"]'),
	};
  };
  
  const isValidDom = (dom) => {
	return Boolean(
	  dom.calculator &&
		dom.slider &&
		dom.sliderWrap &&
		dom.optionsWrap &&
		dom.primaryLabel &&
		dom.qualifier &&
		dom.revenueValue &&
		dom.priceValue &&
		dom.tabs.length
	);
  };
  
  export default function yourInvestmentBlock() {
	const block = document.querySelector('[data-js="your-investment-block"]');
  
	if (!block) {
	  return;
	}
  
	const dom = getDomElements(block);
  
	if (!isValidDom(dom)) {
	  return;
	}
  
	const selectedIndexes = createSelectedIndexes();
  
	const activeTypeState = {
	  value: dom.calculator.dataset.type || "organisations",
	};
  
	const updateCta = (selection) => {
	  if (!dom.cta) {
		return;
	  }
  
	  const nextHref = buildCtaUrl(
		dom.cta.dataset.baseUrl || dom.cta.getAttribute("href") || "",
		{
		  typeKey: activeTypeState.value,
		  membershipLabel: selection.membershipLabel,
		}
	  );
  
	  if (nextHref) {
		dom.cta.href = nextHref;
	  }
  
	  dom.cta.dataset.selectedType = activeTypeState.value;
	  dom.cta.dataset.selectedLabel = selection.membershipLabel;
	};
  
	const render = (typeKey, nextIndex = null) => {
	  const typeConfig = INVESTMENT_CONFIG[typeKey];
  
	  if (!typeConfig) {
		return;
	  }
  
	  const selection = getSelection(
		typeConfig,
		nextIndex === null ? selectedIndexes[typeKey] : nextIndex
	  );
  
	  selectedIndexes[typeKey] = selection.index;
	  activeTypeState.value = typeKey;
  
	  updateCalculatorUi({
		calculator: dom.calculator,
		tabs: dom.tabs,
		typeKey,
		primaryLabelElement: dom.primaryLabel,
		qualifierElement: dom.qualifier,
		revenueValueElement: dom.revenueValue,
		priceValueElement: dom.priceValue,
		selection,
		formatPriceFn: formatPrice,
	  });
  
	  if (selection.showSlider) {
		showSliderMode({
		  slider: dom.slider,
		  sliderWrap: dom.sliderWrap,
		  optionsWrap: dom.optionsWrap,
		  selection,
		});
	  } else {
		showOptionsMode({
		  sliderWrap: dom.sliderWrap,
		  optionsWrap: dom.optionsWrap,
		  selection,
		});
	  }
  
	  updateCta(selection);
	};
  
	const handleTabClick = (event) => {
	  const nextType = event.currentTarget.dataset.type;
  
	  if (!nextType || !INVESTMENT_CONFIG[nextType]) {
		return;
	  }
  
	  render(nextType);
	};
  
	const handleSliderInput = () => {
	  render(activeTypeState.value, Number(dom.slider.value));
	};
  
	const handleOptionsClick = (event) => {
	  const optionButton = event.target.closest('[data-js="investment-option"]');
  
	  if (!optionButton) {
		return;
	  }
  
	  render(activeTypeState.value, Number(optionButton.dataset.optionIndex || 0));
	};
  
	dom.tabs.forEach((tab) => {
	  tab.addEventListener("click", handleTabClick);
	});
  
	dom.slider.addEventListener("input", handleSliderInput);
	dom.optionsWrap.addEventListener("click", handleOptionsClick);
  
	render(activeTypeState.value);
  
	syncStableCardHeight({
	  calculator: dom.calculator,
	  activeType: activeTypeState,
	  selectedIndexes,
	  config: INVESTMENT_CONFIG,
	  render,
	});
  
	window.addEventListener(
	  "resize",
	  debounce(() => {
		syncStableCardHeight({
		  calculator: dom.calculator,
		  activeType: activeTypeState,
		  selectedIndexes,
		  config: INVESTMENT_CONFIG,
		  render,
		});
	  })
	);
  }
